import { useState } from 'react';
import { createPortal } from 'react-dom';
import { SchedulerItem, type SchedulerItemProps } from '@progress/kendo-react-scheduler';
import type { AcuityReservation } from './acuityTypes';
import { ReleaseCountdownChip } from './ReleaseCountdownChip';
import { useSchedule } from '../context/ScheduleContext';

// ─── Per-level visual tokens ───────────────────────────────────────────────

const LEVEL_STYLE: Record<string, { bg: string; border: string; borderStyle: string; iconColor: string; defaultLabel: string }> = {
  urgent:  { bg: 'rgba(220,38,38,0.13)',  border: '#dc2626', borderStyle: 'solid',  iconColor: '#dc2626', defaultLabel: 'Urgent hold' },
  high:    { bg: 'rgba(217,119,6,0.13)',  border: '#d97706', borderStyle: 'solid',  iconColor: '#d97706', defaultLabel: 'Priority hold' },
  routine: { bg: 'rgba(59,130,246,0.13)', border: '#3b82f6', borderStyle: 'dashed', iconColor: '#3b82f6', defaultLabel: 'Reserved' },
};

// ─── Inline SVG icons ──────────────────────────────────────────────────────

function AlertCircleIcon({ color }: { color: string }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function ClockExclamationIcon({ color }: { color: string }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
      <line x1="12" y1="18" x2="12.01" y2="18" />
    </svg>
  );
}

function ClockIcon({ color }: { color: string }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function LockIcon({ color }: { color: string }) {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={color}
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function levelIcon(level: string, color: string) {
  if (level === 'urgent')  return <AlertCircleIcon color={color} />;
  if (level === 'high')    return <ClockExclamationIcon color={color} />;
  return <ClockIcon color={color} />;
}

// ─── Renderer ─────────────────────────────────────────────────────────────

interface Props extends SchedulerItemProps {
  reservation: AcuityReservation;
  warningWindowMinutes: number;
}

export function AcuitySlotRenderer({ reservation, warningWindowMinutes, ...props }: Props) {
  const { privilegedView, handleAcuitySlotClaimed } = useSchedule();
  const [tipPos, setTipPos] = useState<{ x: number; y: number } | null>(null);

  const tokens = LEVEL_STYLE[reservation.acuityLevel] ?? LEVEL_STYLE.routine;
  const displayLabel = reservation.label ?? tokens.defaultLabel;
  const isLocked = !!reservation.lockReason;

  const schedulerEl = (
    <SchedulerItem
      {...props}
      style={{
        ...props.style,
        background: tokens.bg,
        borderColor: tokens.border,
        borderStyle: tokens.borderStyle,
        borderWidth: 2,
        cursor: privilegedView ? 'pointer' : 'default',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          padding: '1px 2px',
          overflow: 'hidden',
          height: '100%',
        }}
      >
        <span style={{ flexShrink: 0, marginTop: 1 }}>
          {isLocked
            ? <LockIcon color={tokens.iconColor} />
            : levelIcon(reservation.acuityLevel, tokens.iconColor)}
        </span>
        <span
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: tokens.border,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}
        >
          {displayLabel}
        </span>
      </div>

      {reservation.releaseAt && (
        <span style={{ position: 'absolute', bottom: 2, right: 3 }}>
          <ReleaseCountdownChip
            releaseAt={reservation.releaseAt}
            warningWindowMinutes={warningWindowMinutes}
          />
        </span>
      )}

      {/* Lock tooltip trigger — sits above the label content via zIndex */}
      {isLocked && (
        <div
          style={{ position: 'absolute', inset: 0, cursor: 'help', zIndex: 2 }}
          onMouseEnter={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
          onMouseMove={(e) => setTipPos({ x: e.clientX, y: e.clientY })}
          onMouseLeave={() => setTipPos(null)}
        />
      )}
    </SchedulerItem>
  );

  const wrapped = privilegedView ? (
    <div
      style={{ display: 'contents' }}
      onClickCapture={(e) => {
        e.stopPropagation();
        handleAcuitySlotClaimed(reservation);
      }}
    >
      {schedulerEl}
    </div>
  ) : schedulerEl;

  return (
    <>
      {wrapped}
      {tipPos && reservation.lockReason && createPortal(
        <div style={{
          position: 'fixed',
          left: tipPos.x + 12,
          top: tipPos.y + 12,
          background: '#1f2937',
          color: '#fff',
          padding: '4px 8px',
          borderRadius: 4,
          fontSize: 12,
          lineHeight: 1.4,
          whiteSpace: 'nowrap',
          zIndex: 99999,
          pointerEvents: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
        }}>
          {reservation.lockReason}
        </div>,
        document.body,
      )}
    </>
  );
}
